import { type InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'

interface UseCustomSelectStateFromInfiniteScrollProviderProps<V, F> {
  scrollProvider: InfiniteScrollProvider<V, F>
  searchText: string
  onSearchTextChange: (newText: string) => unknown
  searchResults?: readonly V[]
}

/**
 * Uses an `InfiniteScrollProvider` to derive the state as well as a
 * pre-filtered, pre-sorted `searchResults` (if `undefined`, uses the
 * scrollers items). Must also provide `searchText` and `onSearchTextChange`.
 *
 * Calling component must be an `observer`.
 *
 * @param scrollProvider
 * @param searchResults
 * @param searchText
 * @param onSearchTextChange
 */
export function useCustomSelectStateFromInfiniteScrollProvider<V, F>({
  scrollProvider,
  searchResults,
  searchText,
  onSearchTextChange,
}: UseCustomSelectStateFromInfiniteScrollProviderProps<V, F>) {
  const inputValue = searchText
  const onInputValueChange = onSearchTextChange
  const isLoading = scrollProvider.more.pending
  const onLoadMore = scrollProvider.more
  const isEoS = scrollProvider.isDone
  return {
    onLoadMore,
    inputValue,
    onInputValueChange,
    isLoading,
    isEoS,
    searchResults: searchResults ?? scrollProvider.items,
  }
}
